import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Webscrape } from '@shared/index';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DrawerModule } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputText } from "primeng/inputtext";
import { Menu, MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';
import { BulkAction, BulkActionBarComponent } from 'src/app/shared/components/bulk-action-bar/bulk-action-bar.component';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule as PrimeTooltipModule } from 'primeng/tooltip';
import { HintMemoryRequiredComponent } from 'src/app/shared/components/ai-bot/utils/hint-memory-required/hint-memory-required.component';
import { SkillIdInputComponent } from 'src/app/shared/components/ai-bot/utils/skill-id-input/skill-id-input.component';
import { SkillUsageListComponent } from 'src/app/shared/components/ai-bot/utils/skill-usage-list/skill-usage-list.component';
import { BotService } from 'src/app/shared/services/bot.service';
import { CounterService } from '@shared-angular/services/counter.service';
import { LoginService } from '@shared-angular/services/login.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { RunningStatus, WebscrapeService } from 'src/app/shared/services/webscrape.service';
import { ManualLinkComponent } from "../../../../../../shared/components/rag-admin/manual-link/manual-link.component";
import { OfflineWebsitesTabDetailComponent } from './offline-websites-tab-detail/offline-websites-tab-detail.component';

@Component({
  imports: [NgClass, 
    FormsModule,
    HintMemoryRequiredComponent,
    TooltipModule,
    SkillIdInputComponent,
    SkillUsageListComponent,
    ManualLinkComponent,
    MenuModule,
    ButtonModule,
    ConfirmDialogModule,
    TableModule,
    BulkActionBarComponent,
    ToggleSwitchModule,
    PrimeTooltipModule,
    InputText,
    InputGroupModule,
    InputGroupAddonModule,
    IconFieldModule,
    InputIconModule,
    InputNumberModule,
    DrawerModule,
    OfflineWebsitesTabDetailComponent
  ],
  templateUrl: './offline-websites-tab.component.html',
  styleUrl: './offline-websites-tab.component.scss',
  providers: [ConfirmationService]
})
export class OfflineWebsitesTabComponent implements OnInit, OnDestroy {
  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'bulk-delete',
        label: $localize`:@@bulk-delete:Löschen`,
        icon: 'ri ri-delete-bin-line',
        command: () => this.deleteSelectedWebscrapes()
      }
    ];
  }

  private runningInterval: number;
  public offlineWebUrl: string = '';
  public offlineWebDescription: string = '';
  public offlineWebDepth: number = 0;
  public runningStatus: RunningStatus[] = [];
  public selectedWebscrapes: Webscrape[] = [];
  public isWebscrapeSelectable = ({ data }: { data: Webscrape }): boolean => !this.scrapeIsRunning(data._id);

  public get allSelected(): boolean {
    const selectableWebscrapes = this.rss.webscrapes.filter(webscrape => !this.scrapeIsRunning(webscrape._id));
    return selectableWebscrapes.length > 0 && this.selectedWebscrapes.length === selectableWebscrapes.length;
  }

  constructor(
    private toastr: ToastrService,
    public service: WebscrapeService,
    private counterService: CounterService,
    public wss: WebscrapeService,
    public rss: RouteShareService,
    public loginService: LoginService,
    private botService: BotService,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService
  ) {
    this.runningInterval = window.setInterval(() => {
      void this.updateRunningStatus();
    }, 10000);
  }

  public async ngOnInit(): Promise<void> {
    await this.updateRunningStatus();
    this.route.queryParams.subscribe(params => {
      if (params['open']) {
        this.showDetailDrawer(params['open'] as string);
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.runningInterval) {
      window.clearInterval(this.runningInterval);
    }
  }


  private async updateRunningStatus() {
    const oldRunningStatus = [...this.runningStatus];
    this.runningStatus = (await this.service.getRunningStatus(this.rss.botConfig._id)).running;
    oldRunningStatus.filter(oldStatus => {
      return this.runningStatus.findIndex(newStatus => newStatus._id == oldStatus._id) < 0;
    }).forEach(async (status) => {
      const newScrape = await this.service.getWebscrape(this.rss.botConfig._id, status._id);
      const index = this.rss.webscrapes.findIndex(scrape => scrape._id == newScrape.scrape._id);
      this.rss.webscrapes[index] = newScrape.scrape;
    });


  }

  public scrapeIsRunning(scrapeId: string): boolean {
    return this.runningStatus.findIndex(status => status._id == scrapeId) >= 0;
  }

  public getRunningCount(scrapeId: string): number {
    const runningStatus = this.runningStatus.find(status => status._id == scrapeId);
    if (runningStatus) {
      return runningStatus.pageCount;
    } else {
      return 0;
    }
  }

  public isConfigValid(): boolean {
    return this.offlineWebDepth >= 0 && this.offlineWebDepth <= 10 && this.offlineWebUrl.trim() != '' && this.isUrlValid(this.offlineWebUrl) && this.offlineWebDescription.trim() != '';
  }

  public async addOfflineWeb() {
    if (!this.isConfigValid()) return;
    const body: { url: string, depth: number, description: string, active: boolean, skillId: string, name: string } = {
      url: this.offlineWebUrl.trim(),
      depth: this.offlineWebDepth,
      description: this.offlineWebDescription.trim(),
      active: true,
      skillId: '',
      name: ''
    };
    body.name = await this.botService.suggestName(this.rss.botConfig._id, body.description);
    body.skillId = await this.botService.suggestSkill(this.rss.botConfig._id, body.description, body.name);
    const res = (await this.service.postWebscrapeBody(this.rss.botConfig._id, body)).scrape;
    if (this.rss.webscrapes.every(scrape => scrape.url != body.url || scrape.botId != this.rss.botConfig._id)) {
      this.rss.webscrapes.push(res);
      this.counterService.addOfflineWebsite(this.rss.botConfig._id);
    }
    this.runningStatus.push({ _id: res._id, pageCount: 0 });

    this.offlineWebDepth = 0;
    this.offlineWebUrl = '';
    this.offlineWebDescription = '';
    this.toastr.success("SAVED", "Offline-Webseite hinzugefügt und wird geladen");
    await this.updateRunningStatus();
    this.showDetailDrawer(res._id);
  }

  public async deleteWebscrapes(offlineWeb: Webscrape, showFeedback = true) {
    await this.service.deleteWebscrapeId(offlineWeb.botId, offlineWeb._id);
    this.counterService.removeOfflineWebsite(this.rss.botConfig._id);
    this.rss.webscrapes = this.rss.webscrapes.filter((scrape: Webscrape) => {
      if (offlineWeb._id == scrape._id) {
        return false;
      } else {
        return true;
      }
    });
    this.selectedWebscrapes = this.selectedWebscrapes.filter(webscrape => webscrape._id !== offlineWeb._id);
    if (showFeedback) {
      await this.loginService.loadMyUser();
      this.toastr.success("SAVED", "Offline-Webseite gelöscht");
    }
  };

  public toggleAll(checked: boolean): void {
    this.selectedWebscrapes = checked
      ? this.rss.webscrapes.filter(webscrape => !this.scrapeIsRunning(webscrape._id))
      : [];
  }

  public deleteSelectedWebscrapes(): void {
    this.confirmationService.confirm({
      header: $localize`:@@bulk-delete-skills-header:Ausgewählte Einträge löschen?`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@bulk-delete-skills-message:Möchtest du die ausgewählten Einträge wirklich löschen?`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete:Löschen`,
        severity: 'danger'
      },
      accept: async () => {
        const webscrapes = [...this.selectedWebscrapes];
        for (const webscrape of webscrapes) {
          await this.deleteWebscrapes(webscrape, false);
        }
        this.selectedWebscrapes = [];
        await this.loginService.loadMyUser();
        this.toastr.success("SAVED", "Offline-Webseiten gelöscht");
      }
    });
  }

  public isUrlValid(url: string): boolean {
    if (url == '') return true;
    return /(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})[/\w .-]*\/?/.test(url);
  }

  public webscrapeContextMenu: MenuItem[] = [];
  public selectedWebscrape: Webscrape | null = null;
  public selectedWebscrapeId: string = '';
  public detailDrawer: boolean = false;

  public showDetailDrawer(webscrapeId: string): void {
    this.selectedWebscrapeId = webscrapeId;
    const webscrape = this.rss.webscrapes.find(s => s._id === webscrapeId);
    if (webscrape) {
      this.rss.webscrape = webscrape;
      this.rss.webscrapeMap.clear();
      for (const keyURL of webscrape.distinctPages) {
        this.rss.webscrapeMap.set(keyURL, webscrape.pages.filter(page => keyURL == page.url));
      }
    }
    this.detailDrawer = true;
  }

  public openWebscrapeMenu(event: Event, menu: Menu, webscrape: Webscrape) {
    this.selectedWebscrape = webscrape;
    this.webscrapeContextMenu = this.buildWebscrapeMenuItems(webscrape);
    menu.toggle(event);
  }

  private buildWebscrapeMenuItems(webscrape: Webscrape): MenuItem[] {
    const disabled = this.scrapeIsRunning(webscrape._id);

    return [
      {
        label: $localize`:@@edit:Bearbeiten`,
        icon: 'ri ri-pencil-fill',
        command: () => this.showDetailDrawer(webscrape._id),
        disabled
      },
      {
        label: $localize`:@@delete:Löschen`,
        icon: 'ri ri-close-large-fill',
        command: () => this.deleteWebscrapes(webscrape),
        disabled
      }
    ];
  }
}
